import * as m from "mithril";
import { NavBar } from '../../components/NavBar';
import AppContext from '../../context/AppContext';
import './PickUpDropOffSelectionPage.scss';
import { MessageProvider } from "../../context/AppMessages";
import TripSearchResultSummaryView from "../../components/TripSearchResultSummaryView";
import buildLanguageChangeActions from "../common/LanguageNavActions";
import { getIntlStringValue } from "../../helpers/StringHelpers";
import CustomSvgIcon from "../../components/CustomSvgIcon";
import { DropOffOrderLineItemDTO, InternationalStringDTO, MoneyDTO, PickUpOrderLineItemDTO, SeatSelectionDTO, TripCustomizationDTO, TripDTO} from "../../apiclient/ApiDtos";
import SearchCriteria from "../../models/SearchCriteria";
import ModalDialog from "../../components/ModalDialog";
import { OrderedSeatsComponent } from "../travellerinfo/SelectedSeatsComponent";

interface Attrs {
    appContext: AppContext,
    searchCriteria: SearchCriteria,
    tripDetails: TripDTO,
    seatSelections: SeatSelectionDTO[],
    selectedPickupOptions: PickUpOrderLineItemDTO[],
    selectedDropOffOptions: DropOffOrderLineItemDTO[],
    messageProvider: MessageProvider,
    errorMessage: InternationalStringDTO | null,
    isBacktracking: boolean,
    tripCustomization?: TripCustomizationDTO,
    onBacktrack: () => void,
    onBacktrackCancelled: () => void,
    onBacktrackConfirmed: () => void,

    onPickUpAddRequested: () => void,
    onPickUpEditRequested: (index) => void,
    onPickUpRemoveRequested: (item: PickUpOrderLineItemDTO) => void,

    onDropOffAddRequested: () => void,
    onDropOffEditRequested: (index) => void,
    onDropOffRemoveRequested: (item: DropOffOrderLineItemDTO) => void,

    onSubmitPickUpDropOff: () => void
}

function PickUpDropOffSelectionMainPage(): m.Component<Attrs> {
    let showingPickUpSelectButtonWarning = false;
    let showingDropOffSelectButtonWarning = false;

    return {
        view: function (vnode) {
            const { tripDetails, onBacktrack, messageProvider } = vnode.attrs;
            let currLang = vnode.attrs.messageProvider.getCurrentLang();
            let notices: InternationalStringDTO[] = vnode.attrs.tripDetails?.notices || [];

            let langChangeActionItems = buildLanguageChangeActions(vnode.attrs.appContext);

            let maxPassengers = vnode.attrs.seatSelections.length;

            let pickUpSelections = vnode.attrs.selectedPickupOptions;
            let dropOffSelections = vnode.attrs.selectedDropOffOptions;

            let totalPassengersForPickUp = pickUpSelections.map(it => it.numberOfSeats).reduce((a, c) => a + c, 0);
            let totalPassengersForDropOff = dropOffSelections.map(it => it.numberOfSeats).reduce((a, c) => a + c, 0);

            let totalAmountForPickUp = pickUpSelections.map(it => it.option.unitFee.amount * it.numberOfSeats).reduce((a, c) => a + c, 0);
            let totalAmountForDropOff = dropOffSelections.map(it => it.option.unitFee.amount * it.numberOfSeats).reduce((a, c) => a + c, 0);

            let sourceLocation = vnode.attrs.tripDetails.departure.location;
            let destinationLocation = vnode.attrs.tripDetails.arrival.location;

            let shouldAlertToDiscardInfo = (pickUpSelections.length > 0 || dropOffSelections.length > 0);


            return m("div.app-layout", [
                m(NavBar, {
                    title: vnode.attrs.appContext.getMessageProvider().get('nav_title.select_pickup_dropoff'),
                    canBackPress: true,
                    actionItems: langChangeActionItems,
                    onBackPressed: () => {
                        vnode.attrs.onBacktrack();
                    }
                }),

                m("div.app-area-below-nav"),

                m("div.app-area-main",
                    [
                        m("div.card shadow-sm",
                            m("div.card-body p-3", [

                                m(TripSearchResultSummaryView, {
                                    tripResult: vnode.attrs.tripDetails,
                                    numberOfSeats: vnode.attrs.searchCriteria.numberOfSeats,
                                    messageProvider: vnode.attrs.messageProvider,
                                    showingDate: true,
                                    hideTripPriceRange: true
                                }),

                                (notices.length > 0) ?
                                    m("div", { class: "alert alert-info mt-2 mb-0 p-2" },
                                        [
                                            m("small.font-weight-bold",
                                                vnode.attrs.appContext.getMessageProvider().get("trip.operator_notices", {
                                                    name: getIntlStringValue(tripDetails.operator.name, currLang)
                                                })
                                            ),
                                            vnode.attrs.tripDetails?.notices.map(notice => {
                                                return m("div", { class: "small" }, "- " + getIntlStringValue(notice, currLang))
                                            })
                                        ]
                                    )
                                    : null,
                            ]),
                            m("div.card-body border-top border-light p-3", [
                                m(OrderedSeatsComponent, {
                                    isSeatingAuto: !vnode.attrs.tripDetails.isSeatSelectionAllowed,
                                    allowCollapse: true,
                                    messageProvider: vnode.attrs.messageProvider,
                                    seatSelections: vnode.attrs.seatSelections
                                })
                            ])
                        ),
                        vnode.attrs.errorMessage != null ?
                            m("div", { class: "d-flex alert alert-warning mx-3 mt-2 mb-0 p-2" },
                                m(CustomSvgIcon, {
                                    iconType: 'exclamation_triangle',
                                    themeCssClass: 'svg-medium svg-warning-dark'
                                }),
                                m("small.ms-1 ml-1 font-weight-bold", currLang == "en" ? vnode.attrs.errorMessage.en : vnode.attrs.errorMessage.my)
                            ) : null,

                        m("div.card",
                            m("div.card-body p-3",
                                [
                                    m("style", "dialog {border:red}"),

                                    m("div.itinerary itinerary-md-two-columns mt-2",
                                        [
                                            m("div", { "class": "waypoint-figure text-body-sm" },
                                                [
                                                    m("div", { "class": "edge-top " }),
                                                    m("div", { "class": "node-active" }),
                                                    m("div", { "class": "edge-bot edge-active-dotted" })
                                                ]
                                            ),
                                            m("div", { "class": "waypoint-details text-body-sm" },
                                                m("div",                          
                                                    getIntlStringValue(sourceLocation.name, currLang),
                                                )
                                            ),
                                            m("div", { "class": "waypoint-figure text-body-sm" },
                                                [
                                                    m("div", { "class": "edge-top edge-active-dotted" }),
                                                    m("div", { "class": "edge-mid edge-active-dotted" }),
                                                    m("div", { "class": "edge-bot edge-active-dotted" })
                                                ]
                                            ),
                                            m("div", { "class": "waypoint-details text-body-sm_" },
                                                m("div.mb-4", [
                                                    pickUpSelections.map((selection, index) => {
                                                        return m(LineItemCard, {
                                                            name: getIntlStringValue(selection.option.name, currLang),
                                                            description: selection.option.description,
                                                            hideNumberOfPerson: (vnode.attrs.searchCriteria.numberOfSeats == 1),
                                                            numberOfSeats: selection.numberOfSeats,
                                                            mode: selection.option.mode,
                                                            address: selection.address,
                                                            unitAmount: selection.option.unitFee,
                                                            isFeeOnPoint: (selection.option.pricingMode ?? 'PER_SEAT') == 'PER_STOP',
                                                            totalAmount: selection.totalFee!,
                                                            onEdit: () => {
                                                                vnode.attrs.onPickUpEditRequested(index)
                                                            },
                                                            onRemove: () => {
                                                                vnode.attrs.onPickUpRemoveRequested(selection);
                                                            },
                                                            messageProvider: messageProvider
                                                        })
                                                    }),

                                                    (maxPassengers > totalPassengersForPickUp) ?
                                                        m(StartSelectionButton, {
                                                            isForAdditional: totalPassengersForPickUp > 0,
                                                            message: (totalPassengersForPickUp > 0 && totalPassengersForPickUp < maxPassengers) ?
                                                                messageProvider.get("way_point_selection.select_for_remaining_travellers", {
                                                                    numOfTravellers: maxPassengers - totalPassengersForPickUp
                                                                })
                                                                :
                                                                messageProvider.get("way_point_selection.choose_pickup_location"),
                                                            showingWarning: showingPickUpSelectButtonWarning,
                                                            onClick: () => {
                                                                vnode.attrs.onPickUpAddRequested();
                                                            }
                                                        }) : null,

                                                    (pickUpSelections.length == 1 && totalPassengersForPickUp == maxPassengers && maxPassengers > 1) ?
                                                        m("div.mt-2 text-muted text-body-sm", messageProvider.get("way_point_selection.single_pickup_location_selected_for_all"))
                                                        : null

                                                ])
                                            ),
                                            m("div", { "class": "waypoint-figure text-body-sm" },
                                                [
                                                    m("div", { "class": "edge-top edge-active-dotted" }),
                                                    m("div", { "class": "node-active" }),
                                                    m("div", { "class": "edge-bot" })
                                                ]
                                            ),
                                            m("div", { "class": "waypoint-details text-body-sm" },
                                                getIntlStringValue(destinationLocation.name, currLang),
                                            ),
                                            m("div", { "class": "waypoint-figure text-body-sm" },
                                                [
                                                    //m("div", { "class": "edge-top edge-active-dotted" }),
                                                    //m("div", { "class": "edge-mid edge-active-dotted" }),
                                                    //m("div", { "class": "edge-bot edge-active-dotted" })
                                                ]
                                            ),
                                            m("div", { "class": "waypoint-details text-body-sm_" },
                                                m("div.mb-4", [
                                                    dropOffSelections.map((selection, index) => {
                                                        return m(LineItemCard, {
                                                            name: getIntlStringValue(selection.option.name, currLang),
                                                            description: selection.option.description,
                                                            hideNumberOfPerson: (vnode.attrs.searchCriteria.numberOfSeats == 1),
                                                            numberOfSeats: selection.numberOfSeats,
                                                            mode: selection.option.mode,
                                                            address: selection.address,
                                                            isFeeOnPoint: (selection.option.pricingMode ?? 'PER_SEAT') == 'PER_STOP',
                                                            unitAmount: selection.option.unitFee,
                                                            totalAmount: selection.totalFee!,
                                                            onEdit: () => {
                                                                vnode.attrs.onDropOffEditRequested(index)
                                                            },
                                                            onRemove: () => {
                                                                vnode.attrs.onDropOffRemoveRequested(selection);
                                                            },
                                                            messageProvider: messageProvider
                                                        })
                                                    }),

                                                    (maxPassengers > totalPassengersForDropOff) ?
                                                        m(StartSelectionButton, {
                                                            isForAdditional: totalPassengersForDropOff > 0,
                                                            message: (totalPassengersForDropOff > 0 && totalPassengersForDropOff < maxPassengers) ?
                                                                messageProvider.get("way_point_selection.select_for_remaining_travellers", {
                                                                    numOfTravellers: maxPassengers - totalPassengersForDropOff
                                                                })
                                                                :
                                                                messageProvider.get("way_point_selection.choose_dropoff_location"),
                                                            showingWarning: showingDropOffSelectButtonWarning,
                                                            onClick: () => {
                                                                vnode.attrs.onDropOffAddRequested();
                                                            }
                                                        }) : null,

                                                    (dropOffSelections.length == 1 && totalPassengersForDropOff == maxPassengers && maxPassengers > 1) ?
                                                        m("div.mt-2 text-muted text-body-sm", messageProvider.get("way_point_selection.single_dropoff_location_selected_for_all"))
                                                        : null

                                                ])
                                            ),
                                        ]
                                    ),
                                ]
                            )
                        ),

                        m(ModalDialog, {
                            title: messageProvider.get("back_to_seat_selection_confirmation_title"),
                            message: shouldAlertToDiscardInfo ? messageProvider.get("back_to_seat_selection_confirm_and_alert_description") : messageProvider.get("back_to_seat_selection_confirmation_description"),
                            shouldOpen: vnode.attrs.isBacktracking,
                            iconType: 'exclamation_circle',
                            iconThemeClass: " svg-medium svg-info",
                            onClosed: () => {
                                vnode.attrs.onBacktrackCancelled()
                            },

                            actionItems: [
                                {
                                    label: "Yes",
                                    onclick: () => {
                                        vnode.attrs.onBacktrackConfirmed()
                                    }
                                },
                                {
                                    label: "No",
                                    onclick: () => {
                                        vnode.attrs.onBacktrackCancelled();
                                    }
                                }

                            ]
                        })
                    ]
                ),
                m("div.app-area-footer",
                    m("div.p-2",
                        m("button.btn btn-primary form-control", {
                            onclick: () => {
                                console.log("Validating selections...");
                                console.debug("TotalPassengersForPickup -- MaxPassengers", totalPassengersForPickUp, maxPassengers)
                                console.debug("TotalPassengersForDropOff -- MaxPassengers", totalPassengersForDropOff, maxPassengers)
                                if (totalPassengersForPickUp != maxPassengers) {
                                    showingPickUpSelectButtonWarning = true;
                                    return;
                                }

                                if (totalPassengersForDropOff != maxPassengers) {
                                    showingDropOffSelectButtonWarning = true;
                                    return;
                                }

                                vnode.attrs.onSubmitPickUpDropOff()
                            }
                        }, messageProvider.get("continue"))
                    )
                )
            ])
        }
    }
}

function StartSelectionButton(): m.Component<{
    isForAdditional: boolean,
    message: string,
    showingWarning: boolean,
    onClick: () => void
}> {
    return {
        oncreate: (vnodeDom) => {
            const el = vnodeDom.dom as HTMLElement;
            const rect = el.getBoundingClientRect();
            const isVisible =
                rect.top >= 0 &&
                rect.bottom <= window.innerHeight;

            if (!isVisible) {
                el.scrollIntoView({ behavior: "smooth", block: "nearest" });
            }
        },
        onupdate: function (vnode) {

            const el = document.getElementById("start-selection-required-warning-id");
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "nearest" });
            }
        },
        view: function (vnode) {
            return m("div.d-flex my-2 p-2 rounded shadow-sm small", {
                id: vnode.attrs.showingWarning ? "start-selection-required-warning-id" : "",
                class: vnode.attrs.showingWarning ? "border border-danger" : "border _border-muted",
                style: vnode.attrs.showingWarning ? "border-width:2px !important;" : "",
                onclick: e => {
                    vnode.attrs.onClick();
                }
            }, [
                m("div", { class: "flex-grow-1" }, [
                    vnode.attrs.isForAdditional ?
                        m(CustomSvgIcon, {
                            iconType: "plus",
                            themeCssClass: "svg-info svg-small"
                        }) : null,

                    m("span.text-primary ms-2 ml-2", vnode.attrs.message)
                ]),
                m("div",
                    m(CustomSvgIcon, {
                        iconType: "caret_arrow_down",
                        themeCssClass: "svg-info svg-small"
                    })
                ),

            ]);
        }
    }
}

function LineItemCard(): m.Component<{
    name: string,
    hideNumberOfPerson: boolean,
    numberOfSeats: number,
    mode: "DOORSTEP" | "FIXED_POINT",
    address: string,
    description: InternationalStringDTO,
    unitAmount: MoneyDTO,
    isFeeOnPoint: boolean,
    totalAmount: MoneyDTO,
    onEdit: () => void,
    onRemove: () => void,
    messageProvider: MessageProvider
}> {
    return {
        view: function (vnode) {
            return m("div.card.mt-2",
                m("div.card-body py-2 small", [
                    m("div.d-flex", [
                        m("div.flex-grow-1", [
                            m("strong.text-secondary.mr-2", vnode.attrs.name),
                            vnode.attrs.hideNumberOfPerson ?
                                null
                                :
                                m("div.badge badge-light-blue font-weight-normal opacity-75 pt-1", {
                                    style: "font-size : 0.75rem;"
                                },
                                    vnode.attrs.numberOfSeats,
                                    " ",
                                    vnode.attrs.numberOfSeats > 1 ?
                                        vnode.attrs.messageProvider.get("way_point_selection.no_of_travellers_unit")
                                        :
                                        vnode.attrs.messageProvider.get("way_point_selection.no_of_traveller_unit")
                                )
                        ]),
                        m("div.text-end text-right text-success", [
                            vnode.attrs.isFeeOnPoint ?
                                [
                                    m("div.text-right text-end", {
                                        style: "min-width: 80px;"
                                    },
                                        (vnode.attrs.unitAmount.amount > 0) ?
                                            m("strong.text-success", "MMK " + vnode.attrs.unitAmount.amount) :
                                            m("strong.text-success", "Free")
                                    )
                                ]
                                :
                                [
                                    (vnode.attrs.unitAmount.amount > 0) ?
                                        m("strong", vnode.attrs.numberOfSeats + " x " + "MMK " + vnode.attrs.unitAmount.amount) :
                                        m("strong", vnode.attrs.numberOfSeats + " x " + "Free")
                                ],

                        ]),
                    ]),

                    vnode.attrs.mode == 'DOORSTEP' ?
                        m("div.text-break",
                            m("span.small text-secondary", vnode.attrs.messageProvider.get("way_point_selection.address") + " - "),
                            vnode.attrs.address)
                        :
                        m("div.text-muted",

                            m("div.",
                                m("span.text-info small font-weight-bold fw-bold", vnode.attrs.messageProvider.get("common.gathering_point")),

                                ( vnode.attrs.description &&
                                    (
                                        (vnode.attrs.messageProvider.getCurrentLang() == "en" && vnode.attrs.description.en != null && vnode.attrs.description.en.trim() != "") ||
                                        (vnode.attrs.messageProvider.getCurrentLang() == "my" && vnode.attrs.description.my != null && vnode.attrs.description.my.trim() != "")
                                    )
                                ) &&
                                m("small.text-secondary ml-1",
                                    " - ",
                                    vnode.attrs.messageProvider.getCurrentLang() == "en" ?
                                        vnode.attrs.description.en
                                        :
                                        vnode.attrs.description.my
                                )
                            )
                        ),
                ]
                ),
                m("div.card-footer py-1 text-right text-body-sm",
                    m("a.text-danger ml-2 me-2 mr-2", {
                        href: "#",
                        onclick: (event) => {
                            event.preventDefault();
                            vnode.attrs.onRemove()
                        }
                    }, m("small", vnode.attrs.messageProvider.get("way_point_selection.remove"))),
                    m("a.text-primary ml-2", {
                        href: "#",
                        onclick: (event) => {
                            event.preventDefault();
                            vnode.attrs.onEdit()
                        }
                    }, m("small", vnode.attrs.messageProvider.get("way_point_selection.change")))
                )
            );
        }
    }
}

export default PickUpDropOffSelectionMainPage;
